import React from 'react';
import Select, { components } from 'react-select';
import styled, { useTheme } from 'styled-components';
import { ParagraphLargeText } from '../../Texts';
import { PHONE_MEDIA_QUERY, useMediaQuery } from '../../../common/responsive';

const Label = styled.label`
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: ${({ theme }) => theme.text.secondary};
`;

const COUNTRIES = [
  'Afghanistan',
  'Albania',
  'Algeria',
  'Andorra',
  'Angola',
  'Antigua and Barbuda',
  'Argentina',
  'Armenia',
  'Australia',
  'Austria',
  'Azerbaijan',
  'Bahamas',
  'Bahrain',
  'Bangladesh',
  'Barbados',
  'Belarus',
  'Belgium',
  'Belize',
  'Benin',
  'Bhutan',
  'Bolivia',
  'Bosnia and Herzegovina',
  'Botswana',
  'Brazil',
  'Brunei',
  'Bulgaria',
  'Burkina Faso',
  'Burundi',
  'Cabo Verde',
  'Cambodia',
  'Cameroon',
  'Canada',
  'Central African Republic',
  'Chad',
  'Chile',
  'China',
  'Colombia',
  'Comoros',
  'Congo (Congo-Brazzaville)',
  'Costa Rica',
  'Côte d’Ivoire',
  'Croatia',
  'Cuba',
  'Cyprus',
  'Czechia',
  'Democratic Republic of the Congo',
  'Denmark',
  'Djibouti',
  'Dominica',
  'Dominican Republic',
  'Ecuador',
  'Egypt',
  'El Salvador',
  'Equatorial Guinea',
  'Eritrea',
  'Estonia',
  'Eswatini',
  'Ethiopia',
  'Fiji',
  'Finland',
  'France',
  'Gabon',
  'Gambia',
  'Georgia',
  'Germany',
  'Ghana',
  'Greece',
  'Grenada',
  'Guatemala',
  'Guinea',
  'Guinea-Bissau',
  'Guyana',
  'Haiti',
  'Honduras',
  'Hungary',
  'Iceland',
  'India',
  'Indonesia',
  'Iran',
  'Iraq',
  'Ireland',
  'Israel',
  'Italy',
  'Jamaica',
  'Japan',
  'Jordan',
  'Kazakhstan',
  'Kenya',
  'Kiribati',
  'Kuwait',
  'Kyrgyzstan',
  'Laos',
  'Latvia',
  'Lebanon',
  'Lesotho',
  'Liberia',
  'Libya',
  'Liechtenstein',
  'Lithuania',
  'Luxembourg',
  'Madagascar',
  'Malawi',
  'Malaysia',
  'Maldives',
  'Mali',
  'Malta',
  'Marshall Islands',
  'Mauritania',
  'Mauritius',
  'Mexico',
  'Micronesia',
  'Moldova',
  'Monaco',
  'Mongolia',
  'Montenegro',
  'Morocco',
  'Mozambique',
  'Myanmar',
  'Namibia',
  'Nauru',
  'Nepal',
  'Netherlands',
  'New Zealand',
  'Nicaragua',
  'Niger',
  'Nigeria',
  'North Korea',
  'North Macedonia',
  'Norway',
  'Oman',
  'Pakistan',
  'Palau',
  'Panama',
  'Papua New Guinea',
  'Paraguay',
  'Peru',
  'Philippines',
  'Poland',
  'Portugal',
  'Qatar',
  'Romania',
  'Russia',
  'Rwanda',
  'Saint Kitts and Nevis',
  'Saint Lucia',
  'Saint Vincent and the Grenadines',
  'Samoa',
  'San Marino',
  'Sao Tome and Principe',
  'Saudi Arabia',
  'Senegal',
  'Serbia',
  'Seychelles',
  'Sierra Leone',
  'Singapore',
  'Slovakia',
  'Slovenia',
  'Solomon Islands',
  'Somalia',
  'South Africa',
  'South Korea',
  'South Sudan',
  'Spain',
  'Sri Lanka',
  'Sudan',
  'Suriname',
  'Sweden',
  'Switzerland',
  'Syria',
  'Taiwan',
  'Tajikistan',
  'Tanzania',
  'Thailand',
  'Timor-Leste',
  'Togo',
  'Tonga',
  'Trinidad and Tobago',
  'Tunisia',
  'Turkey',
  'Turkmenistan',
  'Tuvalu',
  'Uganda',
  'Ukraine',
  'United Arab Emirates',
  'United Kingdom',
  'United States',
  'Uruguay',
  'Uzbekistan',
  'Vanuatu',
  'Vatican City',
  'Venezuela',
  'Vietnam',
  'Yemen',
  'Zambia',
  'Zimbabwe',
  // Common territories/regions often needed for shipping selectors:
  'Åland Islands',
  'American Samoa',
  'Anguilla',
  'Aruba',
  'Bermuda',
  'Bonaire, Sint Eustatius and Saba',
  'British Virgin Islands',
  'Cayman Islands',
  'Channel Islands',
  'Cook Islands',
  'Curaçao',
  'Faroe Islands',
  'French Guiana',
  'French Polynesia',
  'Gibraltar',
  'Greenland',
  'Guadeloupe',
  'Guam',
  'Guernsey',
  'Hong Kong',
  'Isle of Man',
  'Jersey',
  'Kosovo',
  'Macau',
  'Martinique',
  'Mayotte',
  'Montserrrat',
  'New Caledonia',
  'Niue',
  'Norfolk Island',
  'Northern Mariana Islands',
  'Palestine',
  'Puerto Rico',
  'Réunion',
  'Saint Barthélemy',
  'Saint Martin',
  'Saint Pierre and Miquelon',
  'Sint Maarten',
  'South Georgia and the South Sandwich Islands',
  'Turks and Caicos Islands',
  'U.S. Virgin Islands',
  'Wallis and Futuna',
].sort();

const CountrySelectorContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 48px;
  opacity: ${({ $disabled }) => ($disabled ? 0.7 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};
  min-width: 105px;
`;

const InputContainer = styled.div`
  display: flex;
  align-items: center;
  border-radius: 16px;
  height: 48px;
  width: 100%;
  box-sizing: border-box;
  padding: 0px;
`;

const OptionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
`;

const Name = styled(ParagraphLargeText)`
  color: ${({ theme }) => theme.text.regular};
  font-size: 16px;
  font-weight: 400;
  @media ${PHONE_MEDIA_QUERY} {
    font-size: 14px;
  }
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
`;

const SingleValueContainer = styled.div`
  padding-right: 10px;
`;

const DisabledValue = styled.p`
  font-size: 16px;
  font-weight: 600;
  color: hsl(0, 0%, 20%);
`;

const StyledSelectContainer = styled.div`
  flex: 1;
  min-width: 0;
  .react-select__control {
    background-color: ${({ theme }) => theme.background.light};
    border: none;
    box-shadow: none;
    padding: 8px;
    padding-left: 16px;
    min-height: 48px;
  }
`;

// Custom renderers (name only)
const Option = ({ data, ...props }) => (
  <components.Option {...props}>
    <OptionContainer>
      <Name>{data.label}</Name>
    </OptionContainer>
  </components.Option>
);

const SingleValue = ({ data, ...props }) => (
  <components.SingleValue {...props}>
    <SingleValueContainer>
      <Name>{data.label}</Name>
    </SingleValueContainer>
  </components.SingleValue>
);

const CountrySelector = ({ label = 'Country', value, disabled, onChange }) => {
  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);

  const options = React.useMemo(
    () => COUNTRIES.map((c) => ({ value: c, label: c })),
    [],
  );

  const selectedOption = options.find((o) => o.value === value) || null;

  const handleChange = (option) => {
    onChange?.(option ? option.value : null);
  };

  const theme = useTheme();

  return (
    <CountrySelectorContainer $disabled={disabled}>
      <Row>
        <InputContainer>
          <StyledSelectContainer>
            <Label>{label}</Label>
            {options.length < 2 ? (
              <DisabledValue>{selectedOption?.label ?? '—'}</DisabledValue>
            ) : (
              <Select
                className='react-select'
                classNamePrefix='react-select'
                isDisabled={disabled}
                isSearchable
                menuIsOpen={options.length > 1 ? undefined : false}
                styles={{
                  option: (provided, state) => ({
                    ...provided,
                    backgroundColor: theme.background.light,
                    margin: 0,
                    padding: '8px',
                    color: state.isSelected
                      ? theme.primary.accent
                      : theme.text.regular,
                    '@media (hover: hover)': {
                      '&:hover': {
                        backgroundColor: theme.background.secondary,
                      },
                    },
                  }),
                  menu: (provided) => ({
                    ...provided,
                    backgroundColor: theme.background.light,
                    padding: 0,
                    left: isMobile ? -(15 + 8 + 24) : 0,
                    width: isMobile ? 'calc(100vw - 32px)' : '280px',
                    border: 'none',
                    boxShadow: '0 2px 4px rgba(17, 16, 16, 0.4)',
                    borderRadius: '8px',
                  }),
                  control: (provided) => ({
                    ...provided,
                    backgroundColor: theme.background.primary,
                    border: `1px solid ${theme.border.secondary}`,
                    borderRadius: 16,
                    gap: 0,
                    boxShadow: 'none',
                    cursor: 'pointer',
                    paddingRight: '6px',
                    paddingLeft: '6px',
                    minHeight: 'unset',
                  }),
                  valueContainer: (provided) => ({
                    ...provided,
                    padding: 0,
                    margin: 0,
                  }),
                  indicatorSeparator: () => ({ display: 'none' }),
                  dropdownIndicator: (provided) => ({
                    ...provided,
                    color: theme.text.secondary,
                    padding: '0px',
                    minWidth: '0px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }),
                  singleValue: (provided) => ({
                    ...provided,
                    margin: '0 4px 0 0',
                  }),
                }}
                options={options}
                value={selectedOption}
                onChange={(opt) => handleChange(opt)}
                placeholder='Select country'
                components={{ Option, SingleValue }}
              />
            )}
          </StyledSelectContainer>
        </InputContainer>
      </Row>
    </CountrySelectorContainer>
  );
};

export default CountrySelector;
